import React, { useState, useEffect } from 'react';
import { Employee } from '../types/employee';
import { TDLRChecker, TDLRLicenseInfo } from '../utils/tdlrChecker.ts';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/config.ts';
import html2pdf from 'html2pdf.js';

interface TDLRLicenseManagementProps {
  employees: Employee[];
  onRefreshEmployees: () => void;
  onUpdateEmployee?: (employeeId: string, updates: any) => void;
}

const TDLRLicenseManagement: React.FC<TDLRLicenseManagementProps> = ({
  employees,
  onRefreshEmployees,
  onUpdateEmployee
}) => {
  const [loading, setLoading] = useState(false);
  const [checkingAll, setCheckingAll] = useState(false);
  const [checkingIndividual, setCheckingIndividual] = useState<string | null>(null);
  const [results, setResults] = useState<{
    checked: number;
    updated: number;
    errors: Array<{ employee: string; error: string }>;
  } | null>(null);
  const [expiringLicenses, setExpiringLicenses] = useState<Employee[]>([]);
  const [expiredLicenses, setExpiredLicenses] = useState<Employee[]>([]);
  const [individualCheckResult, setIndividualCheckResult] = useState<{
    employee: string;
    result: TDLRLicenseInfo | null;
    error?: string;
  } | null>(null);
  const [showManualEntry, setShowManualEntry] = useState<{
    employeeId: string;
    employeeName: string;
    tdlrNumber: string;
  } | null>(null);
  const [manualExpirationDate, setManualExpirationDate] = useState('');
  const [generatingReport, setGeneratingReport] = useState(false);

  // Get employees with TDLR numbers
  const employeesWithTDLR = employees.filter(emp => emp.tdlrNumber && emp.status === 'active');

  useEffect(() => {
    loadExpirationData();
  }, [employees]);

  const loadExpirationData = async () => {
    setLoading(true);
    try {
      const [expiring, expired] = await Promise.all([
        TDLRChecker.getExpiringLicenses(30),
        TDLRChecker.getExpiredLicenses()
      ]);
      setExpiringLicenses(expiring);
      setExpiredLicenses(expired);
    } catch (error) {
      console.error('Error loading expiration data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckAllLicenses = async () => {
    setCheckingAll(true);
    setResults(null);
    
    try {
      const checkResults = await TDLRChecker.checkAllEmployeeLicenses();
      setResults(checkResults);
      
      // Only refresh expiration lists to avoid full page reload
      await loadExpirationData();
      
    } catch (error) {
      console.error('Error checking all licenses:', error);
      setResults({
        checked: 0,
        updated: 0,
        errors: [{ employee: 'System', error: 'Failed to check licenses' }]
      });
    } finally {
      setCheckingAll(false);
    }
  };

  const handleCheckIndividualLicense = async (employee: Employee) => {
    if (!employee.tdlrNumber) return;
    
    setCheckingIndividual(employee.id);
    setIndividualCheckResult(null);
    
    try {
      const result = await TDLRChecker.checkAndUpdateEmployeeLicense(employee.id, employee.tdlrNumber);
      
      if (result.success && result.licenseInfo) {
        setIndividualCheckResult({
          employee: employee.name,
          result: result.licenseInfo
        });
        
        // Use lightweight update if available, otherwise full refresh
        if (onUpdateEmployee && result.licenseInfo.expirationDate) {
          onUpdateEmployee(employee.id, {
            tdlrExpires: result.licenseInfo.expirationDate,
            updatedAt: new Date()
          });
        } else {
          onRefreshEmployees();
        }
        
        await loadExpirationData();
      } else {
        setIndividualCheckResult({
          employee: employee.name,
          result: null,
          error: result.error
        });
      }
    } catch (error) {
      setIndividualCheckResult({
        employee: employee.name,
        result: null,
        error: error instanceof Error ? error.message : 'Unknown error'
      });
    } finally {
      setCheckingIndividual(null);
    }
  };

  const handleManualEntry = (employee: Employee) => {
    setShowManualEntry({
      employeeId: employee.id,
      employeeName: employee.name || 'Unknown Employee',
      tdlrNumber: employee.tdlrNumber || ''
    });
    setManualExpirationDate(employee.tdlrExpires ? 
      (employee.tdlrExpires instanceof Date ? employee.tdlrExpires : employee.tdlrExpires.toDate())
      .toISOString().split('T')[0] : '');
  };

  const handleManualUpdate = async () => {
    if (!showManualEntry || !manualExpirationDate) return;

    try {
      const expirationDate = new Date(manualExpirationDate);
      const updateData = {
        tdlrExpires: expirationDate,
        updatedAt: new Date()
      };
      
      await updateDoc(doc(db, 'employees', showManualEntry.employeeId), updateData);

      // Use lightweight update if available, otherwise full refresh
      if (onUpdateEmployee) {
        onUpdateEmployee(showManualEntry.employeeId, updateData);
      } else {
        onRefreshEmployees();
      }
      
      await loadExpirationData();
      
      // Close modal
      setShowManualEntry(null);
      setManualExpirationDate('');
    } catch (error) {
      console.error('Error updating manual expiration date:', error);
    }
  };

  const generatePDFReport = async () => {
    setGeneratingReport(true);
    
    try {
      // Get all employees (not just active ones with TDLR)
      const allEmployees = employees.filter(emp => emp.status === 'active');
      const employeesWithTDLR = allEmployees.filter(emp => emp.tdlrNumber);
      const employeesWithoutTDLR = allEmployees.filter(emp => !emp.tdlrNumber);
      
      // Sort by expiration status
      const expiredLicenses = employeesWithTDLR.filter(emp => {
        if (!emp.tdlrExpires) return false;
        const expDate = emp.tdlrExpires instanceof Date ? emp.tdlrExpires : emp.tdlrExpires.toDate();
        return expDate < new Date();
      });
      
      const expiringLicenses = employeesWithTDLR.filter(emp => {
        if (!emp.tdlrExpires) return false;
        const expDate = emp.tdlrExpires instanceof Date ? emp.tdlrExpires : emp.tdlrExpires.toDate();
        const now = new Date();
        const thirtyDaysFromNow = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000));
        return expDate > now && expDate <= thirtyDaysFromNow;
      });
      
      const validLicenses = employeesWithTDLR.filter(emp => {
        if (!emp.tdlrExpires) return true; // Assume valid if no expiration date
        const expDate = emp.tdlrExpires instanceof Date ? emp.tdlrExpires : emp.tdlrExpires.toDate();
        const thirtyDaysFromNow = new Date(new Date().getTime() + (30 * 24 * 60 * 60 * 1000));
        return expDate > thirtyDaysFromNow;
      });

      const reportHtml = `
        <style>
          @media print {
            .no-break {
              page-break-inside: avoid;
            }
            table {
              page-break-inside: auto;
            }
            tr {
              page-break-inside: avoid;
              page-break-after: auto;
            }
            thead {
              display: table-header-group;
            }
            tbody {
              display: table-row-group;
            }
            .section {
              page-break-inside: avoid;
            }
          }
        </style>
        <div style="font-family: Arial, sans-serif; padding: 20px; color: #333;">
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 3px solid #1f2937; padding-bottom: 20px;">
            <h1 style="color: #1f2937; margin: 0; font-size: 28px; font-weight: bold;">7Twenty4 Services LLC</h1>
            <h2 style="color: #dc2626; margin: 20px 0 0 0; font-size: 24px;">TDLR License Status Report</h2>
            <p style="color: #6b7280; margin: 5px 0 0 0; font-size: 14px;">Generated on ${new Date().toLocaleDateString()}</p>
          </div>

          <!-- Summary Statistics -->
          <div style="margin-bottom: 30px;">
            <h3 style="color: #1f2937; margin-bottom: 15px; font-size: 18px;">Summary</h3>
            <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-bottom: 20px;">
              <div style="background: #f3f4f6; padding: 15px; border-radius: 8px; text-align: center;">
                <div style="font-size: 24px; font-weight: bold; color: #2563eb;">${employeesWithTDLR.length}</div>
                <div style="font-size: 12px; color: #6b7280;">Total TDLR Licenses</div>
              </div>
              <div style="background: #fef2f2; padding: 15px; border-radius: 8px; text-align: center;">
                <div style="font-size: 24px; font-weight: bold; color: #dc2626;">${expiredLicenses.length}</div>
                <div style="font-size: 12px; color: #6b7280;">Expired Licenses</div>
              </div>
              <div style="background: #fffbeb; padding: 15px; border-radius: 8px; text-align: center;">
                <div style="font-size: 24px; font-weight: bold; color: #d97706;">${expiringLicenses.length}</div>
                <div style="font-size: 12px; color: #6b7280;">Expiring Soon (30 days)</div>
              </div>
              <div style="background: #f0fdf4; padding: 15px; border-radius: 8px; text-align: center;">
                <div style="font-size: 24px; font-weight: bold; color: #16a34a;">${validLicenses.length}</div>
                <div style="font-size: 12px; color: #6b7280;">Valid Licenses</div>
              </div>
            </div>
          </div>

          ${expiredLicenses.length > 0 ? `
          <!-- Expired Licenses -->
          <div class="section" style="margin-bottom: 30px;">
            <h3 style="color: #dc2626; margin-bottom: 15px; font-size: 18px;">⚠️ Expired TDLR Licenses (${expiredLicenses.length})</h3>
            <table class="no-break" style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead>
                <tr style="background-color: #fef2f2;">
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Employee Name</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">TDLR Number</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Expiration Date</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Days Overdue</th>
                </tr>
              </thead>
              <tbody>
                ${expiredLicenses.map(emp => {
                  const expDate = emp.tdlrExpires instanceof Date ? emp.tdlrExpires : emp.tdlrExpires?.toDate();
                  const daysOverdue = expDate ? Math.ceil((new Date().getTime() - expDate.getTime()) / (1000 * 60 * 60 * 24)) : 'N/A';
                  return `
                    <tr style="page-break-inside: avoid;">
                      <td style="border: 1px solid #ddd; padding: 8px;">${emp.name || 'N/A'}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${emp.tdlrNumber || 'N/A'}</td>
                      <td style="border: 1px solid #ddd; padding: 8px; color: #dc2626; font-weight: bold;">${expDate ? expDate.toLocaleDateString() : 'N/A'}</td>
                      <td style="border: 1px solid #ddd; padding: 8px; color: #dc2626; font-weight: bold;">${daysOverdue} days</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          ${expiringLicenses.length > 0 ? `
          <!-- Expiring Soon -->
          <div class="section" style="margin-bottom: 30px;">
            <h3 style="color: #d97706; margin-bottom: 15px; font-size: 18px;">⏰ TDLR Licenses Expiring Soon (${expiringLicenses.length})</h3>
            <table class="no-break" style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead>
                <tr style="background-color: #fffbeb;">
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Employee Name</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">TDLR Number</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Expiration Date</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Days Remaining</th>
                </tr>
              </thead>
              <tbody>
                ${expiringLicenses.map(emp => {
                  const expDate = emp.tdlrExpires instanceof Date ? emp.tdlrExpires : emp.tdlrExpires?.toDate();
                  const daysRemaining = expDate ? Math.ceil((expDate.getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24)) : 'N/A';
                  return `
                    <tr style="page-break-inside: avoid;">
                      <td style="border: 1px solid #ddd; padding: 8px;">${emp.name || 'N/A'}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${emp.tdlrNumber || 'N/A'}</td>
                      <td style="border: 1px solid #ddd; padding: 8px; color: #d97706; font-weight: bold;">${expDate ? expDate.toLocaleDateString() : 'N/A'}</td>
                      <td style="border: 1px solid #ddd; padding: 8px; color: #d97706; font-weight: bold;">${daysRemaining} days</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          <!-- All Valid Licenses -->
          <div class="section" style="margin-bottom: 30px;">
            <h3 style="color: #16a34a; margin-bottom: 15px; font-size: 18px;">✅ Valid TDLR Licenses (${validLicenses.length})</h3>
            <table class="no-break" style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead>
                <tr style="background-color: #f0fdf4;">
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Employee Name</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">TDLR Number</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Expiration Date</th>
                </tr>
              </thead>
              <tbody>
                ${validLicenses.map(emp => {
                  const expDate = emp.tdlrExpires instanceof Date ? emp.tdlrExpires : emp.tdlrExpires?.toDate();
                  return `
                    <tr style="page-break-inside: avoid;">
                      <td style="border: 1px solid #ddd; padding: 8px;">${emp.name || 'N/A'}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${emp.tdlrNumber || 'N/A'}</td>
                      <td style="border: 1px solid #ddd; padding: 8px;">${expDate ? expDate.toLocaleDateString() : 'Not Available'}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
          </div>

          ${employeesWithoutTDLR.length > 0 ? `
          <!-- Missing TDLR Numbers -->
          <div class="section" style="margin-bottom: 30px;">
            <h3 style="color: #6b7280; margin-bottom: 15px; font-size: 18px;">❌ Employees Missing TDLR Numbers (${employeesWithoutTDLR.length})</h3>
            <table class="no-break" style="width: 100%; border-collapse: collapse; font-size: 12px;">
              <thead>
                <tr style="background-color: #f9fafb;">
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Employee Name</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Hire Date</th>
                  <th style="border: 1px solid #ddd; padding: 10px; text-align: left;">Status</th>
                </tr>
              </thead>
              <tbody>
                ${employeesWithoutTDLR.map(emp => `
                  <tr style="page-break-inside: avoid;">
                    <td style="border: 1px solid #ddd; padding: 8px;">${emp.name || 'N/A'}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${emp.hireDate ? (emp.hireDate instanceof Date ? emp.hireDate : emp.hireDate.toDate()).toLocaleDateString() : 'N/A'}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${emp.status || 'N/A'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>
          ` : ''}

          <!-- Footer -->
          <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #e5e7eb; text-align: center; color: #6b7280; font-size: 12px;">
            <p>This report was generated automatically by the 7Twenty4 Management System</p>
          </div>
        </div>
      `;

      // Generate PDF
      const opt = {
        margin: 0.5,
        filename: `TDLR_License_Report_${new Date().toISOString().split('T')[0]}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          letterRendering: true
        },
        jsPDF: { 
          unit: 'in', 
          format: 'letter', 
          orientation: 'portrait' 
        }
      };

      await html2pdf().set(opt).from(reportHtml).save();
      
    } catch (error) {
      console.error('Error generating PDF report:', error);
      alert('Error generating PDF report. Please try again.');
    } finally {
      setGeneratingReport(false);
    }
  };

  const formatDate = (date: Date | null | undefined) => {
    if (!date) return 'N/A';
    return date.toLocaleDateString();
  };

  const getDaysUntilExpiration = (date: Date | null | undefined) => {
    if (!date) return null;
    const now = new Date();
    const diffTime = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getExpirationStatus = (date: Date | null | undefined) => {
    if (!date) return { status: 'unknown', color: 'text-gray-500' };
    
    const days = getDaysUntilExpiration(date);
    if (days === null) return { status: 'unknown', color: 'text-gray-500' };
    
    if (days < 0) return { status: 'expired', color: 'text-red-600 font-semibold' };
    if (days <= 30) return { status: 'expiring', color: 'text-orange-600 font-semibold' };
    return { status: 'valid', color: 'text-green-600' };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">TDLR License Management</h2>
        <div className="flex space-x-3">
          <button
            onClick={generatePDFReport}
            disabled={generatingReport || employees.length === 0}
            className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{generatingReport ? 'Generating PDF...' : 'Generate PDF Report'}</span>
          </button>
          <button
            onClick={handleCheckAllLicenses}
            disabled={checkingAll || employeesWithTDLR.length === 0}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {checkingAll ? 'Checking All Licenses...' : 'Check All TDLR Licenses'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-blue-600">{employeesWithTDLR.length}</div>
          <div className="text-sm text-gray-600">Total TDLR Licenses</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-red-600">{expiredLicenses.length}</div>
          <div className="text-sm text-gray-600">Expired Licenses</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-orange-600">{expiringLicenses.length}</div>
          <div className="text-sm text-gray-600">Expiring Soon (30 days)</div>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="text-2xl font-bold text-green-600">
            {employeesWithTDLR.length - expiredLicenses.length - expiringLicenses.length}
          </div>
          <div className="text-sm text-gray-600">Valid Licenses</div>
        </div>
      </div>

      {/* Check Results */}
      {results && (
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Last Check Results</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="text-center">
              <div className="text-xl font-bold text-blue-600">{results.checked}</div>
              <div className="text-sm text-gray-600">Licenses Checked</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-green-600">{results.updated}</div>
              <div className="text-sm text-gray-600">Records Updated</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-red-600">{results.errors.length}</div>
              <div className="text-sm text-gray-600">Errors</div>
            </div>
          </div>
          
          {results.errors.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-red-600 mb-2">Errors:</h4>
              <div className="max-h-32 overflow-y-auto space-y-1">
                {results.errors.map((error, index) => (
                  <div key={index} className="text-sm text-red-500 bg-red-50 p-2 rounded">
                    <strong>{error.employee}:</strong> {error.error}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Individual Check Result */}
      {individualCheckResult && (
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-3">
            License Check Result: {individualCheckResult.employee}
          </h3>
          
          {individualCheckResult.error ? (
            <div className="text-red-600 bg-red-50 p-3 rounded">
              <strong>Error:</strong> {individualCheckResult.error}
            </div>
          ) : individualCheckResult.result ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <strong>Name:</strong> {individualCheckResult.result.name}
              </div>
              <div>
                <strong>License Type:</strong> {individualCheckResult.result.licenseType}
              </div>
              <div>
                <strong>Status:</strong> {individualCheckResult.result.status}
              </div>
              <div>
                <strong>County:</strong> {individualCheckResult.result.county}
              </div>
              <div>
                <strong>Expiration Date:</strong> {
                  individualCheckResult.result.expirationDate 
                    ? formatDate(individualCheckResult.result.expirationDate)
                    : 'Not found'
                }
              </div>
              <div>
                <strong>Last Checked:</strong> {formatDate(individualCheckResult.result.lastChecked)}
              </div>
            </div>
          ) : (
            <div className="text-gray-600">No license information found.</div>
          )}
          
          <button
            onClick={() => setIndividualCheckResult(null)}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800"
          >
            Close
          </button>
        </div>
      )}

      {/* Expired Licenses Alert */}
      {expiredLicenses.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-red-800 mb-3">
            ⚠️ Expired TDLR Licenses ({expiredLicenses.length})
          </h3>
          <div className="space-y-2">
            {expiredLicenses.map(employee => (
              <div key={employee.id} className="flex justify-between items-center bg-white p-2 rounded">
                <div>
                  <span className="font-medium">{employee.name}</span>
                  <span className="text-sm text-gray-600 ml-2">
                    (TDLR: {employee.tdlrNumber})
                  </span>
                </div>
                <div className="text-sm text-red-600">
                  Expired {Math.abs(getDaysUntilExpiration(employee.tdlrExpires) || 0)} days ago
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Expiring Soon Licenses Alert */}
      {expiringLicenses.length > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
          <h3 className="text-lg font-medium text-orange-800 mb-3">
            ⏰ TDLR Licenses Expiring Soon ({expiringLicenses.length})
          </h3>
          <div className="space-y-2">
            {expiringLicenses.map(employee => (
              <div key={employee.id} className="flex justify-between items-center bg-white p-2 rounded">
                <div>
                  <span className="font-medium">{employee.name}</span>
                  <span className="text-sm text-gray-600 ml-2">
                    (TDLR: {employee.tdlrNumber})
                  </span>
                </div>
                <div className="text-sm text-orange-600">
                  Expires in {getDaysUntilExpiration(employee.tdlrExpires)} days
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All TDLR Licenses Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">All TDLR Licenses</h3>
        </div>
        
        {employeesWithTDLR.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            No employees with TDLR license numbers found.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    TDLR Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expiration Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {employeesWithTDLR.map(employee => {
                  const expirationStatus = getExpirationStatus(employee.tdlrExpires);
                  const daysUntilExpiration = getDaysUntilExpiration(employee.tdlrExpires);
                  
                  return (
                    <tr key={employee.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{employee.name}</div>
                        <div className="text-sm text-gray-500">{employee.jobTitle}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {employee.tdlrNumber}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className={expirationStatus.color}>
                          {formatDate(employee.tdlrExpires)}
                        </div>
                        {daysUntilExpiration !== null && (
                          <div className="text-xs text-gray-500">
                            {daysUntilExpiration < 0 
                              ? `${Math.abs(daysUntilExpiration)} days ago`
                              : `${daysUntilExpiration} days remaining`
                            }
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          expirationStatus.status === 'expired' ? 'bg-red-100 text-red-800' :
                          expirationStatus.status === 'expiring' ? 'bg-orange-100 text-orange-800' :
                          expirationStatus.status === 'valid' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {expirationStatus.status === 'expired' ? 'Expired' :
                           expirationStatus.status === 'expiring' ? 'Expiring Soon' :
                           expirationStatus.status === 'valid' ? 'Valid' :
                           'Unknown'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleCheckIndividualLicense(employee)}
                          disabled={checkingIndividual === employee.id}
                          className="text-blue-600 hover:text-blue-900 disabled:opacity-50"
                        >
                          {checkingIndividual === employee.id ? 'Checking...' : 'Auto Check'}
                        </button>
                        <button
                          onClick={() => handleManualEntry(employee)}
                          className="text-green-600 hover:text-green-900"
                        >
                          Manual Update
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>


      {/* Manual Entry Modal */}
      {showManualEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Update TDLR Expiration Date
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee
                </label>
                <div className="text-sm text-gray-900">{showManualEntry.employeeName}</div>
                <div className="text-xs text-gray-500">TDLR: {showManualEntry.tdlrNumber}</div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expiration Date
                </label>
                <input
                  type="date"
                  value={manualExpirationDate}
                  onChange={(e) => setManualExpirationDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <div className="text-sm text-yellow-800">
                  <strong>Manual Verification Required:</strong><br/>
                  Please verify this date by checking the TDLR website directly before updating.
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowManualEntry(null);
                  setManualExpirationDate('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleManualUpdate}
                disabled={!manualExpirationDate}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Update Expiration Date
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TDLRLicenseManagement;
